import React, { useState, useMemo } from 'react';
import Button from '../common/Button';
import AddressInput from '../common/AddressInput';
import { useJobRoles } from '../../hooks/useUserQueries';
import { validateAgeForRole, validateGdcNumber } from '../../utils/validations';
import './BasicInfo.css';

const BasicInfo = ({ data = {}, onNext, isSubmitting = false }) => {
  const roleType = data.roleType || 'employee';
  
  // Fetch job roles from backend
  const { data: jobRolesData, isLoading: isLoadingJobRoles, isError: isJobRolesError } = useJobRoles();
  
  // Filter and sort active job roles
  const jobRoles = useMemo(() => {
    if (!jobRolesData) return [];
    return jobRolesData
      .filter(role => role.is_active)
      .sort((a, b) => a.job_role_name.localeCompare(b.job_role_name));
  }, [jobRolesData]);
  
  const [formData, setFormData] = useState({
    roleType: roleType,
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    email: data.email || '',
    password: data.password || '',
    passwordConfirm: data.passwordConfirm || '',
    contactNo: data.contactNo || '',
    address: data.address || '',
    latitude: data.latitude || null,
    longitude: data.longitude || null,
    dob: data.dob || '',
    // Employee fields
    jobRole: data.jobRole || '',
    travelMiles: data.travelMiles || '',
    gdcNumber: data.gdcNumber || '',
    // Employer fields
    registeredNumber: data.registeredNumber || '',
    practiceName: data.practiceName || '',
    ...data
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For GDC number, only allow digits
    let processedValue = value;
    if (name === 'gdcNumber') {
      // Remove any non-digit characters
      processedValue = value.replace(/\D/g, '');
      // Limit to 6 digits maximum
      if (processedValue.length > 6) {
        processedValue = processedValue.slice(0, 6);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLocationSelect = (locationData) => {
    setFormData(prev => ({
      ...prev,
      address: locationData.address,
      latitude: locationData.latitude,
      longitude: locationData.longitude,
    }));
    
    // Clear address error if present
    if (errors.address) {
      setErrors(prev => ({
        ...prev,
        address: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (!formData.passwordConfirm) {
      newErrors.passwordConfirm = 'Please confirm your password';
    } else if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = "Passwords don't match";
    }

    if (!formData.contactNo.trim()) {
      newErrors.contactNo = 'Phone number is required';
    } else if (!formData.contactNo.startsWith('+')) {
      newErrors.contactNo = 'Phone number must start with + (e.g., +1234567890)';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required';
    }

    // Employee-specific validations
    if (formData.roleType === 'employee') {
      // Validate job role - check if it exists in fetched job roles
      if (!formData.jobRole) {
        newErrors.jobRole = 'Job role is required';
      } else if (jobRoles.length > 0) {
        const selectedRole = jobRoles.find(role => role.job_role_key === formData.jobRole);
        if (!selectedRole) {
          newErrors.jobRole = 'Please select a valid job role';
        }
      }
      
      // Validate age for selected role
      if (formData.jobRole && formData.dob && jobRoles.length > 0) {
        const selectedRole = jobRoles.find(role => role.job_role_key === formData.jobRole);
        if (selectedRole) {
          const ageValidation = validateAgeForRole(formData.dob, selectedRole.job_role_name);
          if (!ageValidation.isValid) {
            newErrors.dob = ageValidation.error;
          }
        }
      }
      
      if (!formData.travelMiles) {
        newErrors.travelMiles = 'Please select your travel distance';
      }
      
      // GDC number is required for all employees
      if (!formData.gdcNumber || !formData.gdcNumber.trim()) {
        newErrors.gdcNumber = 'GDC number is required';
      } else {
        // Validate GDC number format (5 or 6 digits)
        if (!/^\d{5,6}$/.test(formData.gdcNumber.trim())) {
          newErrors.gdcNumber = 'GDC number must be 5 or 6 digits';
        } else {
          // Additional validation based on job role if available
          if (formData.jobRole && jobRoles.length > 0) {
            const selectedRole = jobRoles.find(role => role.job_role_key === formData.jobRole);
            if (selectedRole) {
              const gdcValidation = validateGdcNumber(formData.gdcNumber, selectedRole.job_role_name);
              if (!gdcValidation.isValid) {
                newErrors.gdcNumber = gdcValidation.error;
              }
            }
          }
        }
      }
    }

    // Employer-specific validations
    if (formData.roleType === 'employer') {
      if (!formData.practiceName.trim()) {
        newErrors.practiceName = 'Practice/Clinic name is required';
      }
      if (!formData.registeredNumber.trim()) {
        newErrors.registeredNumber = 'Company registration number is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Convert travel distance to integer for employees
      const submitData = { ...formData };
      if (submitData.roleType === 'employee' && submitData.travelMiles) {
        // Extract number from range (e.g., "0-10" -> 10, "100+" -> 100)
        const milesValue = submitData.travelMiles.includes('+') 
          ? parseInt(submitData.travelMiles.replace('+', ''))
          : parseInt(submitData.travelMiles.split('-')[1] || submitData.travelMiles);
        submitData.travel_miles = milesValue;
      }
      
      // Map to backend format
      const backendData = {
        role_type: submitData.roleType,
        first_name: submitData.firstName,
        last_name: submitData.lastName,
        email: submitData.email,
        password: submitData.password,
        password_confirm: submitData.passwordConfirm,
        contact_no: submitData.contactNo,
        address: submitData.address,
        latitude: submitData.latitude,
        longitude: submitData.longitude,
        dob: submitData.dob,
      };

      if (submitData.roleType === 'employee') {
        // formData.jobRole already contains job_role_key (set from dropdown)
        backendData.job_role_key = submitData.jobRole;
        backendData.travel_miles = submitData.travel_miles;
        backendData.gdc_number = submitData.gdcNumber;
      } else {
        // Backend expects practice_name and registered_number for employers
        backendData.practice_name = submitData.practiceName;
        backendData.registered_number = submitData.registeredNumber;
      }

      onNext(backendData);
    }
  };

  return (
    <div className="basic-info">
      <form onSubmit={handleSubmit} className="basic-info__form">
        {/* Row 1: Name fields */}
        <div className="basic-info__row basic-info__row--two-col">
          <div className="basic-info__field">
            <label htmlFor="firstName" className="basic-info__label">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={`basic-info__input ${errors.firstName ? 'basic-info__input--error' : ''}`}
              placeholder="First name"
            />
            {errors.firstName && (
              <span className="basic-info__error">{errors.firstName}</span>
            )}
          </div>

          <div className="basic-info__field">
            <label htmlFor="lastName" className="basic-info__label">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={`basic-info__input ${errors.lastName ? 'basic-info__input--error' : ''}`}
              placeholder="Last name"
            />
            {errors.lastName && (
              <span className="basic-info__error">{errors.lastName}</span>
            )}
          </div>
        </div>

        {/* Row 2: Email, Phone, DOB */}
        <div className="basic-info__row">
          <div className="basic-info__field">
            <label htmlFor="email" className="basic-info__label">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`basic-info__input ${errors.email ? 'basic-info__input--error' : ''}`}
              placeholder="email@example.com"
            />
            {errors.email && (
              <span className="basic-info__error">{errors.email}</span>
            )}
          </div>

          <div className="basic-info__field">
            <label htmlFor="contactNo" className="basic-info__label">
              Phone
            </label>
            <input
              type="tel"
              id="contactNo"
              name="contactNo"
              value={formData.contactNo}
              onChange={handleChange}
              className={`basic-info__input ${errors.contactNo ? 'basic-info__input--error' : ''}`}
              placeholder="+44..."
            />
            {errors.contactNo && (
              <span className="basic-info__error">{errors.contactNo}</span>
            )}
          </div>

          <div className="basic-info__field">
            <label htmlFor="dob" className="basic-info__label">
              Date of Birth
            </label>
            <input
              type="date"
              id="dob"
              name="dob"
              value={formData.dob}
              onChange={handleChange}
              className={`basic-info__input ${errors.dob ? 'basic-info__input--error' : ''}`}
              max={new Date().toISOString().split('T')[0]}
            />
            {errors.dob && (
              <span className="basic-info__error">{errors.dob}</span>
            )}
          </div>
        </div>

        {/* Row 3: Passwords */}
        <div className="basic-info__row basic-info__row--two-col">
          <div className="basic-info__field">
            <label htmlFor="password" className="basic-info__label">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`basic-info__input ${errors.password ? 'basic-info__input--error' : ''}`}
              placeholder="Min. 8 characters"
            />
            {errors.password && (
              <span className="basic-info__error">{errors.password}</span>
            )}
          </div>

          <div className="basic-info__field">
            <label htmlFor="passwordConfirm" className="basic-info__label">
              Confirm Password
            </label>
            <input
              type="password"
              id="passwordConfirm"
              name="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={handleChange}
              className={`basic-info__input ${errors.passwordConfirm ? 'basic-info__input--error' : ''}`}
              placeholder="Retype password"
            />
            {errors.passwordConfirm && (
              <span className="basic-info__error">{errors.passwordConfirm}</span>
            )}
          </div>
        </div>

        {formData.roleType === 'employee' && (
          <>
            {/* Row 4: Job Role, Travel, GDC */}
            <div className="basic-info__row">
              <div className="basic-info__field">
                <label htmlFor="jobRole" className="basic-info__label">
                  Job Role
                </label>
                <select
                  id="jobRole"
                  name="jobRole"
                  value={formData.jobRole}
                  onChange={handleChange}
                  disabled={isLoadingJobRoles || isJobRolesError}
                  className={`basic-info__select ${errors.jobRole ? 'basic-info__select--error' : ''}`}
                >
                  <option value="">
                    {isLoadingJobRoles ? 'Loading job roles...' : isJobRolesError ? 'Failed to load job roles' : 'Select role'}
                  </option>
                  {jobRoles.map(role => (
                    <option key={role.id} value={role.job_role_key}>
                      {role.job_role_name}
                    </option>
                  ))}
                </select>
                {errors.jobRole && (
                  <span className="basic-info__error">{errors.jobRole}</span>
                )}
                {isJobRolesError && !errors.jobRole && (
                  <span className="basic-info__error">Failed to load job roles. Please refresh the page.</span>
                )}
              </div>

              <div className="basic-info__field">
                <label htmlFor="travelMiles" className="basic-info__label">
                  Travel Distance
                </label>
                <select
                  id="travelMiles"
                  name="travelMiles"
                  value={formData.travelMiles}
                  onChange={handleChange}
                  className={`basic-info__select ${errors.travelMiles ? 'basic-info__select--error' : ''}`}
                >
                  <option value="">Select miles</option>
                  <option value="0-10">0-10 miles</option>
                  <option value="11-25">11-25 miles</option>
                  <option value="26-50">26-50 miles</option>
                  <option value="51-75">51-75 miles</option>
                  <option value="76-100">76-100 miles</option>
                  <option value="100+">100+ miles</option>
                </select>
                {errors.travelMiles && (
                  <span className="basic-info__error">{errors.travelMiles}</span>
                )}
              </div>

              <div className="basic-info__field">
                <label htmlFor="gdcNumber" className="basic-info__label">
                  GDC Number <span className="basic-info__required">*</span>
                </label>
                <input
                  type="text"
                  id="gdcNumber"
                  name="gdcNumber"
                  value={formData.gdcNumber}
                  onChange={handleChange}
                  inputMode="numeric"
                  pattern="[0-9]{5,6}"
                  maxLength={6}
                  required
                  className={`basic-info__input ${errors.gdcNumber ? 'basic-info__input--error' : ''}`}
                  placeholder="5-6 digits"
                />
                {errors.gdcNumber && (
                  <span className="basic-info__error">{errors.gdcNumber}</span>
                )}
              </div>
            </div>
          </>
        )}

        {formData.roleType === 'employer' && (
          <>
            <div className="basic-info__row basic-info__row--full">
              <div className="basic-info__field">
                <label htmlFor="practiceName" className="basic-info__label">
                  Practice/Clinic Name
                </label>
                <input
                  type="text"
                  id="practiceName"
                  name="practiceName"
                  value={formData.practiceName}
                  onChange={handleChange}
                  className={`basic-info__input ${errors.practiceName ? 'basic-info__input--error' : ''}`}
                  placeholder="Practice or clinic name"
                />
                {errors.practiceName && (
                  <span className="basic-info__error">{errors.practiceName}</span>
                )}
              </div>
            </div>
            <div className="basic-info__row basic-info__row--full">
              <div className="basic-info__field">
                <label htmlFor="registeredNumber" className="basic-info__label">
                  Company Registration Number
                </label>
                <input
                  type="text"
                  id="registeredNumber"
                  name="registeredNumber"
                  value={formData.registeredNumber}
                  onChange={handleChange}
                  className={`basic-info__input ${errors.registeredNumber ? 'basic-info__input--error' : ''}`}
                  placeholder="Company registration number"
                />
                {errors.registeredNumber && (
                  <span className="basic-info__error">{errors.registeredNumber}</span>
                )}
              </div>
            </div>
          </>
        )}

        {/* Row 5: Address */}
        <div className="basic-info__row basic-info__row--full">
          <div className="basic-info__field">
            <label htmlFor="address" className="basic-info__label">
              Address
            </label>
            <AddressInput
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              onLocationSelect={handleLocationSelect}
              placeholder="Enter your full address"
              error={errors.address}
              className={errors.address ? 'address-input--error' : ''}
            />
          </div>
        </div>

        <div className="basic-info__actions">
          <Button 
            type="submit" 
            variant="primary" 
            size="large"
            className="basic-info__button"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BasicInfo;
